import * as React from 'react';
import { connect } from 'react-redux';
import { State, Page } from '../State';
import URLX from '../Url';
import shallowEquals from '../shallow-equals';
import { resolveSyncIfLoadedOrThrow } from '../resolve-component';

type RequiredProps = {};

type ConnectedProps = {
  currentTheme: string;
  currentPage: Page;
};

type Props = RequiredProps & ConnectedProps;

/**
 * Note that this should not be a class method. It should be a static method on the class.
 * Also note that it's not possible to not remount when the user is going from one page type
 * to another. That is, it's not possible to use this feature to not unmount the category page
 * component when going to the product page component.
 *
 * Use this if you want to disable remounting or if you want a different remount strategy than
 * when the url path changes.
 */
export type MountController = {
  componentShouldRemount: (props: Page, nextProps: Page) => boolean;
};

function isMountController(component: any): component is MountController {
  const controller: MountController = component;
  return !!controller.componentShouldRemount;
}

// This class exists as a memoized version of its props. Since props passed to Layout
// can change without the current page changes, we want to avoid re-rendering the
// current page when it hasn't changed.
class CurrentPage extends React.Component<Props> {
  Component: React.ComponentType<any>;
  scrollPosition?: number;
  constructor(props: Props) {
    super(props);
    this.Component = resolveSyncIfLoadedOrThrow(props.currentPage.componentName, props.currentTheme);
    this.scrollPosition = props.currentPage.scrollPosition;
  }
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      this.props.currentPage.componentName !== nextProps.currentPage.componentName ||
      this.props.currentTheme !== nextProps.currentTheme
    ) {
      this.Component = resolveSyncIfLoadedOrThrow(nextProps.currentPage.componentName, nextProps.currentTheme);
    } else {
      // We default to always remount if the path of the url changes. So going from /category/product-a to
      // /category/product-b will remount, but going from /category/product-a?i=1 to /category/product-a?i=2
      // will not remount.
      const NextComponent = resolveSyncIfLoadedOrThrow(nextProps.currentPage.componentName, nextProps.currentTheme);
      let shouldRemount = (remountProps: Page, remountNextProps: Page) =>
        remountProps.url !== remountNextProps.url &&
        new URLX(remountProps.url).pathname !== new URLX(remountNextProps.url).pathname;
      if (isMountController(NextComponent)) {
        shouldRemount = NextComponent.componentShouldRemount;
      }
      if (shouldRemount(this.props.currentPage, nextProps.currentPage)) {
        this.Component = (props: any) => <NextComponent {...props} />;
      }
    }

    if (this.props.currentPage !== nextProps.currentPage) {
      this.scrollPosition = nextProps.currentPage.scrollPosition;
    } else {
      this.scrollPosition = undefined;
    }
  }
  shouldComponentUpdate(newProps: Props) {
    const shouldUpdate = !shallowEquals(this.props, newProps);
    if (!shouldUpdate) {
      console.info('Ignoring update to CurrentPage');
    }
    return shouldUpdate;
  }
  componentDidUpdate() {
    if (this.scrollPosition !== undefined) {
      document.body.scrollTop = this.scrollPosition;

      if (document.documentElement) {
        document.documentElement.scrollTop = this.scrollPosition;
      }
    }
  }
  render() {
    return <this.Component {...this.props.currentPage} />;
  }
}

export default connect(
  (state: State): ConnectedProps => ({
    currentTheme: state.currentTheme,
    currentPage: state.currentPage,
  }),
)(CurrentPage);
